import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { createCustomerOrder, getAvailableDates, getAvailableCoupons } from '../../lib/customerApi';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, Package, ArrowRight, Tag, Ticket, X, Percent, Banknote, Gift } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const CustomerCart = () => {
  const { items, updateQuantity, removeItem, clearCart, totalAmount } = useCart();
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [datesRes, couponsRes] = await Promise.all([
        getAvailableDates(),
        getAvailableCoupons(),
      ]);
      setAvailableDates(datesRes.data);
      setAvailableCoupons(couponsRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const calculateDiscount = () => {
    if (!selectedCoupon) return 0;

    const couponType = selectedCoupon.coupon_type;
    const couponValue = selectedCoupon.value;
    const targetProductId = selectedCoupon.target_product_id;

    if (couponType === 'percentage') {
      if (targetProductId) {
        // 対象商品のみに適用
        const targetItem = items.find(item => item.id === targetProductId);
        if (targetItem) {
          return (targetItem.price * targetItem.quantity) * (couponValue / 100);
        }
        return 0;
      } else {
        return totalAmount * (couponValue / 100);
      }
    } else if (couponType === 'fixed_amount') {
      return Math.min(couponValue, totalAmount);
    } else if (couponType === 'free_sample') {
      return 0; // 無料サンプルは金額に影響しない
    }
    return 0;
  };

  const finalAmount = Math.max(0, totalAmount - calculateDiscount());

  const handleSelectCoupon = (coupon) => {
    // 対象商品チェック
    if (coupon.target_product_id) {
      const hasTargetProduct = items.some(item => item.id === coupon.target_product_id);
      if (!hasTargetProduct) {
        toast.error('クーポン対象商品がありません');
        return;
      }
    }
    setSelectedCoupon(coupon);
    setCouponDialogOpen(false);
    toast.success(`クーポン「${coupon.name}」を適用しました`, { duration: 2500 });
  };

  const handleRemoveCoupon = () => {
    setSelectedCoupon(null);
    toast.success('クーポンを解除しました', { duration: 2500 });
  };

  const handleSubmitOrder = async () => {
    if (items.length === 0) {
      toast.error('カートに商品がありません');
      return;
    }

    if (!deliveryDate) {
      toast.error('配送希望日を選択してください');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        items: items.map(item => ({
          item_id: item.id,
          item_type: item.item_type,
          quantity: item.quantity,
        })),
        notes,
        delivery_date: deliveryDate || null,
        coupon_id: selectedCoupon?.id || null,
      };

      await createCustomerOrder(orderData);
      toast.success('ご注文を受け付けました', { duration: 2500 });
      clearCart();
      navigate('/customer/orders');
    } catch (error) {
      const message = error.response?.data?.detail || '注文の送信に失敗しました';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCouponValue = (coupon) => {
    switch (coupon.coupon_type) {
      case 'percentage':
        return `${coupon.value}% OFF`;
      case 'fixed_amount':
        return `¥${coupon.value.toLocaleString()} OFF`;
      case 'free_sample':
        return `${coupon.value}個 無料`;
      default:
        return coupon.value;
    }
  };

  const getCouponIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <Percent className="h-4 w-4" />;
      case 'fixed_amount':
        return <Banknote className="h-4 w-4" />;
      case 'free_sample':
        return <Gift className="h-4 w-4" />;
      default:
        return <Ticket className="h-4 w-4" />;
    }
  };

  if (items.length === 0) {
    return (
      <div className="space-y-6" data-testid="customer-cart-empty">
        <h1 className="text-2xl font-bold text-slate-900">カート</h1>
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShoppingCart className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">カートに商品がありません</p>
            <Button onClick={() => navigate('/customer/catalog')} className="bg-blue-600 hover:bg-blue-700">
              商品カタログへ
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-cart">
      <h1 className="text-2xl font-bold text-slate-900">カート</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.map((item) => (
            <Card key={`${item.id}-${item.item_type}`} className="border-slate-200">
              <CardContent className="p-3">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {item.item_type === 'sale' ? <Tag className="h-8 w-8 text-red-300" /> : <Package className="h-8 w-8 text-slate-300" />}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-sm">{item.name}</h3>
                          {item.item_type === 'sale' && <Badge className="bg-red-100 text-red-800 text-xs">特売</Badge>}
                        </div>
                        {(item.origin || item.spec) && (
                          <p className="text-xs text-slate-500">
                            {item.origin}{item.origin && item.spec && ' / '}{item.spec}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeItem(item.id, item.item_type)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, item.item_type, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-medium text-sm">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.id, item.item_type, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                        {/* 単位表示を非表示 */}
                      </div>

                      <div className="text-right">
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button variant="outline" className="w-full" onClick={() => navigate('/customer/catalog')}>
            買い物を続ける
          </Button>
        </div>

        <div>
          <Card className="border-slate-200 sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">ご注文内容</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>配送希望日</Label>
                <Select value={deliveryDate} onValueChange={setDeliveryDate}>
                  <SelectTrigger>
                    <SelectValue placeholder="日付を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map((d) => (
                      <SelectItem key={d.date} value={d.date}>
                        {format(new Date(d.date), 'MM月dd日(E)', { locale: ja })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!deliveryDate && (
                  <p className="text-xs text-red-500">※ 配送希望日は必須です</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>申し送り事項</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="ご要望やメモがあればご記入ください"
                  rows={3}
                />
              </div>

              {/* クーポン選択 */}
              <div className="space-y-2">
                <Label>クーポン</Label>
                {selectedCoupon ? (
                  <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      {getCouponIcon(selectedCoupon.coupon_type)}
                      <div>
                        <p className="text-sm font-medium text-purple-900">{selectedCoupon.name}</p>
                        <p className="text-xs text-purple-600">{formatCouponValue(selectedCoupon)}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                      onClick={handleRemoveCoupon}
                      data-testid="remove-coupon-btn"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-slate-500"
                    onClick={() => setCouponDialogOpen(true)}
                    disabled={availableCoupons.length === 0}
                    data-testid="select-coupon-btn"
                  >
                    <Ticket className="h-4 w-4 mr-2" />
                    {availableCoupons.length > 0 ? 'クーポンを選択' : 'クーポンなし'}
                  </Button>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">商品点数</span>
                  <span>{items.length}点</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">数量合計</span>
                  <span>{items.reduce((sum, i) => sum + i.quantity, 0)}個</span>
                </div>
                {selectedCoupon && calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm text-purple-600">
                    <span>クーポン割引</span>
                    <span className="font-mono">-¥{calculateDiscount().toLocaleString()}</span>
                  </div>
                )}
                {selectedCoupon && selectedCoupon.coupon_type === 'free_sample' && (
                  <div className="flex justify-between text-sm text-purple-600">
                    <span>無料サンプル</span>
                    <span className="font-mono">{selectedCoupon.target_product_name || '対象商品'} x{selectedCoupon.value}</span>
                  </div>
                )}
              </div>

              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmitOrder}
                disabled={submitting || !deliveryDate}
                data-testid="submit-order-btn"
              >
                {submitting ? '送信中...' : '注文を確定する'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>

              <p className="text-xs text-slate-500 text-center">
                注文確定後、担当者より確認のご連絡をいたします
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* クーポン選択ダイアログ */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="max-w-md" data-testid="coupon-select-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-purple-600" />
              クーポンを選択
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {availableCoupons.length === 0 ? (
              <p className="text-center text-slate-500 py-4">利用可能なクーポンがありません</p>
            ) : (
              availableCoupons.map((coupon) => {
                const hasTargetProduct = !coupon.target_product_id || items.some(item => item.id === coupon.target_product_id);
                return (
                  <div
                    key={coupon.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      hasTargetProduct
                        ? 'hover:bg-purple-50 hover:border-purple-300'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    onClick={() => hasTargetProduct && handleSelectCoupon(coupon)}
                    data-testid={`coupon-option-${coupon.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getCouponIcon(coupon.coupon_type)}
                        <div>
                          <p className="font-medium text-slate-900">{coupon.name}</p>
                          <p className="text-sm text-purple-600">{formatCouponValue(coupon)}</p>
                        </div>
                      </div>
                      {!hasTargetProduct && (
                        <Badge variant="secondary" className="text-xs">対象外</Badge>
                      )}
                    </div>
                    {coupon.target_product_name && (
                      <p className="text-xs text-slate-500 mt-1">
                        対象: {coupon.target_product_name}
                      </p>
                    )}
                    {coupon.expiration_date && (
                      <p className="text-xs text-slate-400 mt-1">
                        有効期限: {coupon.expiration_date}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
